import React, { memo, useCallback } from "react";

import {
  Button,
  Center,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useApolloClient, useMutation } from "@apollo/client";
import {
  DeleteMessageMutation,
  DeleteMessageMutationVariables,
  MessageFragment,
} from "gql/graphql";
import DELETE_MESSAGE_MUTATION from "modules/messaging/_networking/delete-message";
import { usePubSub } from "modules/providers/pub-sub-provider";
import MESSAGE_FRAGMENT from "modules/messaging/_networking/message-fragment";
import { useOverlay } from "modules/providers/overlay-provider";

interface IProps {
  messageId: string;
}

const DeleteMessageModal = ({ messageId }: IProps) => {
  const { publish } = usePubSub();
  const { closeModal } = useOverlay();
  const { cache } = useApolloClient();
  const message = cache.readFragment<MessageFragment>({
    id: `Message:${messageId}`,
    fragment: MESSAGE_FRAGMENT,
    fragmentName: "Message",
  });

  const [deleteMessageAPI] = useMutation<
    DeleteMessageMutation,
    DeleteMessageMutationVariables
  >(DELETE_MESSAGE_MUTATION);

  const onDeleteClicked = useCallback(() => {
    publish("message-updated", { ...message, deleted: true });
    deleteMessageAPI({
      variables: {
        id: messageId,
      },
    });
    closeModal();
  }, [closeModal, deleteMessageAPI, message, messageId, publish]);

  return (
    <Modal isOpen onClose={closeModal} size="xs" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader>
          <Center>
            <Text>Delete Message</Text>
          </Center>
        </ModalHeader>
        <ModalBody>
          <VStack>
            <Text>Are you sure you want to delete this message?</Text>
            <HStack>
              <Button
                variant="cancel"
                height={8}
                w={28}
                onClick={(e) => {
                  e.stopPropagation();
                  closeModal();
                }}
              >
                No
              </Button>
              <Button height={8} w={28} onClick={onDeleteClicked}>
                Yes
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(DeleteMessageModal);
